import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/firebase/auth'
import { authErrorMessage } from '@/lib/firebase/auth-errors'

/**
 * Shown when the console is opened from a sign-in link on a browser that
 * doesn't remember the address it was sent to — Firebase needs it to redeem the link.
 */
export function EmailLinkConfirm() {
  const { emailLinkStatus, completeEmailLinkSignIn, cancelEmailLinkSignIn } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (emailLinkStatus === 'completing') {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Signing you in…
      </div>
    )
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await completeEmailLinkSignIn(email)
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <p className="text-sm text-muted-foreground">
        Confirm the email address this sign-in link was sent to.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="link-email">Email</Label>
        <Input
          id="link-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy}>
        {busy && <Loader2 className="size-4 animate-spin" />}
        Sign in
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full"
        onClick={cancelEmailLinkSignIn}
        disabled={busy}
      >
        Use another sign-in method
      </Button>
    </form>
  )
}
